import axios from 'axios';

import { environment } from '../environments/environment';

import type {
  ConfirmApplePayAuthorizedTransactionRequestQuery,
  ConfirmApplePayDirectPayTransactionRequestQuery,
  CreateApplePaySessionValidationRequestQuery,
  CreateCreditCardTransactionSessionRequestQuery,
  GetChainStoreInvoiceRequest,
  InitEasyPayTransactionRequestQuery,
  InitJKOPayTransactionRequestQuery,
  InitLinePayTransactionRequestQuery,
  InitTaiwanPayTransactionRequestQuery,
} from './request-types';
import type {
  ConfirmApplePayAuthorizedTransactionResponse,
  ConfirmApplePayDirectPayTransactionResponse,
  ConfirmAuthorizedTransactionResponse,
  ConfirmDirectPaymentTransactionResponse,
  ConfirmEasyWalletTransactionResponse,
  ConfirmTaiwanPayTransactionResponse,
  CreateApplePaySessionValidationResponse,
  CreateCreditCardTransactionSessionResponse,
  GetChainStoreInvoiceResponse,
  InitEasyPayTransactionResponse,
  InitJKOPayTransactionResponse,
  InitLinePayTransactionResponse,
  InitTaiwanPayTransactionResponse,
} from './response-types';
import type { BulletinBoard, CustomBulletinBoard } from '@autopass-web/types';

export const axiosInstance = axios.create({
  baseURL: environment.userApiEndpoint,
  timeout: 60 * 1000,
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

export const dpassAxiosInstance = axios.create({
  baseURL: environment.dpassApiEndpoint,
  timeout: 60 * 1000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TRANSACTION_TYPE_CHAIN_STORE = 'chain_store';

class Client {
  fetchChainStoreInvoice = async ({ invoiceId }: GetChainStoreInvoiceRequest) =>
    axiosInstance.get<GetChainStoreInvoiceResponse>(
      `/v3/invoices/${invoiceId}`,
    );

  createCreditCardTransactionSession = ({
    invoiceId,
    email = undefined,
    receipt,
  }: CreateCreditCardTransactionSessionRequestQuery) =>
    axiosInstance.post<CreateCreditCardTransactionSessionResponse>(
      `/v3/invoices/${invoiceId}/invoice_transactions`,
      {
        email,
        receipt,
      },
    );

  getAutopassPaymentUrl = ({ invoiceId }: { invoiceId: string }) =>
    axiosInstance.get<{ data: { payment_link: string } }>(
      '/v3/invoices/payment_link/autopass',
      {
        params: {
          invoice_id: invoiceId,
        },
      },
    );

  getAutoPartnerPaymentUrl = ({ invoiceId }: { invoiceId: string }) =>
    axiosInstance.get<{ data: { payment_link: string } }>(
      '/v3/invoices/payment_link/auto_partner',
      {
        params: {
          invoice_id: invoiceId,
        },
      },
    );

  initLinePayTransaction = ({
    invoiceId,
    email,
    receipt,
    callbackUrl,
  }: InitLinePayTransactionRequestQuery) =>
    axiosInstance.post<InitLinePayTransactionResponse>(
      '/v3/invoices/line_pay/init',
      {
        invoice_id: invoiceId,
        email,
        receipt,
        callback_url: callbackUrl,
      },
    );

  initEasyWalletTransaction = async ({
    invoiceId,
    email,
    receipt,
    callbackUrl,
  }: InitEasyPayTransactionRequestQuery) =>
    axiosInstance.post<InitEasyPayTransactionResponse>(
      '/v3/invoices/easy_wallet/init',
      {
        invoice_id: invoiceId,
        email,
        receipt,
        callback_url: callbackUrl,
      },
    );
  initJKOPayTransaction = async ({
    invoiceId,
    email,
    receipt,
    callbackUrl,
  }: InitJKOPayTransactionRequestQuery) =>
    axiosInstance.post<InitJKOPayTransactionResponse>(
      '/v3/invoices/jkopay/init',
      {
        invoice_id: invoiceId,
        email,
        receipt,
        callback_url: callbackUrl,
      },
    );

  confirmAuthorizedTransaction = async ({
    merchantTransactionId,
    invoiceId,
    token,
    serviceTransactionId,
  }: {
    merchantTransactionId: string;
    invoiceId: string;
    token: string;
    serviceTransactionId: string;
  }) =>
    axiosInstance.post<ConfirmAuthorizedTransactionResponse>(
      `/v3/invoices/${invoiceId}/invoice_transactions/${serviceTransactionId}/auth`,
      {
        token,
        transaction_id: merchantTransactionId,
      },
    );
  confirmDirectPaymentTransaction = async ({
    merchantTransactionId,
    invoiceId,
    token,
    serviceTransactionId,
  }: {
    merchantTransactionId: string;
    invoiceId: string;
    token: string;
    serviceTransactionId: string;
  }) =>
    axiosInstance.post<ConfirmDirectPaymentTransactionResponse>(
      `/v3/invoices/${invoiceId}/invoice_transactions/${serviceTransactionId}/pay`,
      {
        token,
        transaction_id: merchantTransactionId,
      },
    );

  createApplePaySessionValidation = async ({
    sessionUrl,
  }: CreateApplePaySessionValidationRequestQuery) =>
    axiosInstance.post<CreateApplePaySessionValidationResponse>(
      '/v3/apple_pay/session_validation',
      {
        session_url: sessionUrl,
        transaction_type: TRANSACTION_TYPE_CHAIN_STORE,
      },
    );

  confirmApplePayDirectPayTransaction = async ({
    invoiceId,
    applePayPaymentInfo,
    email,
    receipt,
  }: ConfirmApplePayDirectPayTransactionRequestQuery) =>
    axiosInstance.post<ConfirmApplePayDirectPayTransactionResponse>(
      `/v3/invoices/${invoiceId}/pay_by_apple_pay`,
      {
        apple_pay_payment_info: applePayPaymentInfo,
        billing_contact: {
          email_address: email,
        },
        type: 'web',
        receipt,
      },
    );

  confirmApplePayAuthorizedTransaction = async ({
    invoiceId,
    applePayPaymentInfo,
    email,
    receipt,
  }: ConfirmApplePayAuthorizedTransactionRequestQuery) =>
    axiosInstance.post<ConfirmApplePayAuthorizedTransactionResponse>(
      `/v3/invoices/${invoiceId}/auth_by_apple_pay`,
      {
        apple_pay_payment_info: applePayPaymentInfo,
        billing_contact: {
          email_address: email,
        },
        type: 'web',
        receipt,
      },
    );

  confirmEasyWalletTransaction = async ({
    transactionId,
  }: {
    transactionId: string;
  }) =>
    axiosInstance.post<ConfirmEasyWalletTransactionResponse>(
      `/v3/invoices/easy_wallet/confirm`,
      {
        transaction_id: transactionId,
      },
    );

  confirmJKOPayTransaction = async ({
    transactionId,
  }: {
    transactionId: string;
  }) =>
    axiosInstance.post<ConfirmEasyWalletTransactionResponse>(
      `/v3/invoices/jkopay/confirm`,
      {
        transaction_id: transactionId,
      },
    );

  initTaiwanPayTransaction = async ({
    invoiceId,
    email,
    receipt,
    callbackUrl,
  }: InitTaiwanPayTransactionRequestQuery) =>
    axiosInstance.post<InitTaiwanPayTransactionResponse>(
      '/v3/invoices/taiwan_pay/init',
      {
        invoice_id: invoiceId,
        email,
        receipt,
        callback_url: callbackUrl,
      },
    );

  confirmTaiwanPayTransaction = async ({
    transactionId,
  }: {
    transactionId: string;
  }) =>
    axiosInstance.post<ConfirmTaiwanPayTransactionResponse>(
      `/v3/invoices/taiwan_pay/confirm`,
      {
        transaction_id: transactionId,
      },
    );

  fetchBulletinBoards = <
    TData extends Record<
      string,
      Array<BulletinBoard | CustomBulletinBoard<unknown>>
    >,
  >({
    codes,
    partnerId,
    distributor,
  }: {
    codes: string;
    partnerId?: string;
    distributor?: string;
  }) =>
    axiosInstance.get<{
      data: TData;
    }>(`/v3/bulletin_boards`, {
      params: { codes, partner_id: partnerId, distributor },
    });
}

const client = new Client();

export default client;
